import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Car, Plus, Search, Filter, MoreHorizontal, AlertTriangle,
  Calendar, Gauge, Trash2, Eye, ChevronRight, RefreshCw,
  TrendingUp, TrendingDown, Clock, Euro
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import CreateVehicleModal from "../../components/fleet/CreateVehicleModal";
import BulkLinkVehiclesDialog from "../../components/engagements/BulkLinkVehiclesDialog";
import { Sparkles } from "lucide-react";
import {
  VEHICLE_STATUS_LABELS,
  ACQUISITION_TYPE_LABELS,
  MILEAGE_STATUS_COLORS,
  MILEAGE_STATUS_DOT_COLORS,
  formatMileage,
  formatCurrency,
  formatDate,
  getDaysUntilLabel,
  getStatusColor,
} from "../../lib/fleet-constants";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function VehiclesList() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  
  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [acquisitionFilter, setAcquisitionFilter] = useState("all");
  const [mileageAlertFilter, setMileageAlertFilter] = useState("all");
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkLinkModal, setShowBulkLinkModal] = useState(false);
  const [candidatesCount, setCandidatesCount] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, vehicle: null });
  
  const token = localStorage.getItem("token");

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", 20);
      
      if (search) params.append("search", search);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (acquisitionFilter !== "all") params.append("acquisition_type", acquisitionFilter);
      if (mileageAlertFilter === "alert") params.append("mileage_alert", "true");
      
      const response = await axios.get(`${API}/fleet/vehicles?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setVehicles(response.data.data || []);
      setTotalPages(response.data.pages || 1);
    } catch (err) {
      console.error("Error fetching vehicles:", err);
      toast.error("Erreur lors du chargement des véhicules");
    } finally {
      setLoading(false);
    }
  }, [token, page, search, statusFilter, acquisitionFilter, mileageAlertFilter]);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fleet/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (err) {
      console.error("Error fetching dashboard:", err);
    }
  }, [token]);

  // Scanne les contrats-véhicule non liés
  const fetchCandidatesCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/engagements/vehicle-candidates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCandidatesCount(res.data?.count || 0);
    } catch (err) {
      // silencieux
    }
  }, [token]);

  useEffect(() => {
    fetchVehicles();
    fetchDashboard();
    fetchCandidatesCount();
  }, [fetchVehicles, fetchDashboard, fetchCandidatesCount]);

  const handleDelete = async () => {
    if (!deleteDialog.vehicle) return;
    
    try {
      await axios.delete(`${API}/fleet/vehicles/${deleteDialog.vehicle.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Véhicule déplacé vers la corbeille");
      setDeleteDialog({ open: false, vehicle: null });
      fetchVehicles();
      fetchDashboard();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleVehicleCreated = () => {
    setShowCreateModal(false);
    fetchVehicles();
    fetchDashboard();
  };

  // KPI Cards
  const KPICards = () => (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Véhicules actifs</p>
              <p className="text-2xl font-bold text-blue-900">{dashboard?.active_vehicles || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-500 flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-medium">À restituer (90j)</p>
              <p className="text-2xl font-bold text-orange-900">{dashboard?.returning_soon_count || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-orange-500 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium">Alerte km</p>
              <p className="text-2xl font-bold text-red-900">{dashboard?.mileage_alert_count || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-red-500 flex items-center justify-center">
              <Gauge className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-emerald-600 font-medium">Coût mensuel</p>
              <p className="text-2xl font-bold text-emerald-900">{formatCurrency(dashboard?.total_monthly_cost || 0)}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Euro className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Mobile Vehicle Card
  const VehicleCard = ({ vehicle }) => (
    <Card 
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => navigate(`/fleet/vehicles/${vehicle.id}`)}
      data-testid={`vehicle-card-${vehicle.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-semibold text-slate-900">{vehicle.brand} {vehicle.model}</h3>
            <p className="text-sm text-slate-500">{vehicle.registration}</p>
          </div>
          <Badge className={getStatusColor(vehicle.status)}>
            {VEHICLE_STATUS_LABELS[vehicle.status] || vehicle.status}
          </Badge>
        </div>
        
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-slate-500">Type</p>
            <p className="font-medium">{ACQUISITION_TYPE_LABELS[vehicle.acquisition_type] || vehicle.acquisition_type}</p>
          </div>
          <div>
            <p className="text-slate-500">Conducteur</p>
            <p className="font-medium">{vehicle.driver_name || "-"}</p>
          </div>
          <div>
            <p className="text-slate-500">Kilométrage</p>
            <p className="font-medium">{formatMileage(vehicle.current_mileage)}</p>
          </div>
          <div>
            <p className="text-slate-500">Restitution</p>
            <div className="flex items-center gap-1">
              {vehicle.days_until_return !== null && vehicle.days_until_return !== undefined ? (
                <>
                  <span className={`w-2 h-2 rounded-full ${
                    vehicle.days_until_return <= 30 ? "bg-red-500" :
                    vehicle.days_until_return <= 90 ? "bg-orange-500" : "bg-green-500"
                  }`} />
                  <span className="font-medium">{getDaysUntilLabel(vehicle.days_until_return)}</span>
                </>
              ) : (
                <span>-</span>
              )}
            </div>
          </div>
        </div>
        
        {vehicle.mileage_status && vehicle.mileage_status !== "green" && (
          <div className="mt-3 pt-3 border-t">
            <Badge className={MILEAGE_STATUS_COLORS[vehicle.mileage_status]}>
              <AlertTriangle className="w-3 h-3 mr-1" />
              Alerte kilométrage
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Parc Auto</h1>
              <p className="text-sm text-slate-500">{dashboard?.total_vehicles || 0} véhicule(s)</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { fetchVehicles(); fetchDashboard(); fetchCandidatesCount(); }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Actualiser</span>
            </Button>
            {candidatesCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBulkLinkModal(true)}
                className="border-blue-500 text-blue-700 hover:bg-blue-50 relative"
                data-testid="detect-vehicles-btn"
              >
                <Sparkles className="w-4 h-4 mr-2 text-blue-600" />
                <span className="hidden sm:inline">Détecter dans les contrats</span>
                <span className="sm:hidden">Détecter</span>
                <Badge className="ml-2 bg-blue-600 text-white h-5 min-w-5 px-1.5 text-xs">{candidatesCount}</Badge>
              </Button>
            )}
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={() => setShowCreateModal(true)}
              data-testid="add-vehicle-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un véhicule
            </Button>
          </div>
        </div>
      </div>
      
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        {/* KPIs */}
        <KPICards />
        
        {/* Filters */}
        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher (marque, modèle, immatriculation...)"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-10"
                  data-testid="vehicle-search"
                />
              </div>
            </div>
            
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger data-testid="filter-status">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="active">Actif</SelectItem>
                <SelectItem value="returning_soon">À restituer</SelectItem>
                <SelectItem value="returned">Restitué</SelectItem>
                <SelectItem value="sold">Vendu</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={acquisitionFilter} onValueChange={(v) => { setAcquisitionFilter(v); setPage(1); }}>
              <SelectTrigger data-testid="filter-acquisition">
                <SelectValue placeholder="Type de contrat" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="lld">LLD</SelectItem>
                <SelectItem value="loa">LOA</SelectItem>
                <SelectItem value="credit_bail">Crédit-bail</SelectItem>
                <SelectItem value="owned">Propriété</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={mileageAlertFilter} onValueChange={(v) => { setMileageAlertFilter(v); setPage(1); }}>
              <SelectTrigger data-testid="filter-mileage">
                <SelectValue placeholder="Alerte km" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="alert">Avec alerte km</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : vehicles.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucun véhicule</h3>
              <p className="text-slate-500 mb-4">
                {search || statusFilter !== "all" || acquisitionFilter !== "all" 
                  ? "Aucun véhicule ne correspond à vos critères de recherche."
                  : candidatesCount > 0
                    ? `${candidatesCount} contrat(s) véhicule identifié(s) dans vos engagements — importez-les en un clic.`
                    : "Commencez par ajouter un véhicule à votre parc."}
              </p>
              {!search && statusFilter === "all" && acquisitionFilter === "all" && (
                <div className="flex justify-center gap-2 flex-wrap">
                  {candidatesCount > 0 && (
                    <Button 
                      onClick={() => setShowBulkLinkModal(true)} 
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="empty-detect-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Importer depuis mes contrats ({candidatesCount})
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setShowCreateModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter manuellement
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block bg-white rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Véhicule</TableHead>
                    <TableHead className="font-semibold">Immatriculation</TableHead>
                    <TableHead className="font-semibold">Type</TableHead>
                    <TableHead className="font-semibold">Conducteur</TableHead>
                    <TableHead className="font-semibold">Kilométrage</TableHead>
                    <TableHead className="font-semibold">Restitution</TableHead>
                    <TableHead className="font-semibold">Statut</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vehicles.map((vehicle) => (
                    <TableRow 
                      key={vehicle.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/fleet/vehicles/${vehicle.id}`)}
                      data-testid={`vehicle-row-${vehicle.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                            <Car className="w-5 h-5 text-slate-500" />
                          </div>
                          <div>
                            <p className="font-medium">{vehicle.brand} {vehicle.model}</p>
                            <p className="text-sm text-slate-500">{vehicle.internal_code}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">{vehicle.registration}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {ACQUISITION_TYPE_LABELS[vehicle.acquisition_type] || vehicle.acquisition_type}
                        </Badge>
                      </TableCell>
                      <TableCell>{vehicle.driver_name || "-"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {vehicle.mileage_status && (
                            <span className={`w-2 h-2 rounded-full ${MILEAGE_STATUS_DOT_COLORS[vehicle.mileage_status]}`} />
                          )}
                          {formatMileage(vehicle.current_mileage)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {vehicle.days_until_return !== null && vehicle.days_until_return !== undefined ? (
                          <span className={`font-medium ${
                            vehicle.days_until_return <= 30 ? "text-red-600" :
                            vehicle.days_until_return <= 90 ? "text-orange-600" : "text-slate-600"
                          }`}>
                            {getDaysUntilLabel(vehicle.days_until_return)}
                          </span>
                        ) : "-"}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(vehicle.status)}>
                          {VEHICLE_STATUS_LABELS[vehicle.status] || vehicle.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/fleet/vehicles/${vehicle.id}`); }}>
                              <Eye className="w-4 h-4 mr-2" />
                              Voir la fiche
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, vehicle }); }}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {/* Mobile Cards */}
            <div className="lg:hidden grid grid-cols-1 gap-4">
              {vehicles.map((vehicle) => (
                <VehicleCard key={vehicle.id} vehicle={vehicle} />
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Précédent
                </Button>
                <span className="flex items-center px-4 text-sm text-slate-600">
                  Page {page} sur {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Suivant
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Create Modal */}
      <CreateVehicleModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleVehicleCreated}
      />

      {/* Bulk Link Vehicles Dialog */}
      <BulkLinkVehiclesDialog
        open={showBulkLinkModal}
        onOpenChange={setShowBulkLinkModal}
        token={token}
        onSuccess={() => {
          fetchVehicles();
          fetchDashboard();
          fetchCandidatesCount();
        }}
      />
      
      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, vehicle: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce véhicule ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le véhicule <strong>{deleteDialog.vehicle?.brand} {deleteDialog.vehicle?.model}</strong> ({deleteDialog.vehicle?.registration}) 
              sera déplacé vers la corbeille. Vous pourrez le restaurer pendant 30 jours.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
